import * as yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { API as handoverAPI } from '../api';
import { API as yachtsAPI } from '../../yachts/api';
import { API as usersNauticaAPI } from '../../users_nautica/api';
import { TypeOptions } from './Create.conf';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

const PriorityOptions = [
  { label: 'URGENT', id: 'URGENT' },
  { label: 'MEDIUM', id: 'MEDIUM' },
  { label: 'LOW', id: 'LOW' },
];

const StatusOptions = [
  { label: 'OPEN', id: 'OPEN' },
  { label: 'CLOSED', id: 'CLOSED' },
];

export const FilterConf = {
  schema: yup.object({
    type: yup.mixed().nullable(),
    id_department: yup.array().nullable(),
    id_team: yup.array().nullable(),
    user_id: yup.array().nullable(),
    id_yacht: yup.array().nullable(),
    priority: yup.array().nullable(),
    status: yup.mixed().nullable(),
    start_date: yup
      .mixed()
      .nullable()
      .transform((value) =>
        value && dayjs(value).isValid()
          ? dayjs(value).format('YYYY-MM-DDTHH:mm:ss')
          : null
      )
      .test('is-valid', 'Invalid date', function (value) {
        if (value) return !!dayjs(value).isValid();
        return true;
      }),
    end_date: yup
      .mixed()
      .nullable()
      .transform((value) =>
        value && dayjs(value).isValid()
          ? dayjs(value).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : null
      )
      .test(
        'end-after-start',
        "End date can't be before Start date",
        function (value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;
          return dayjs(value).isSameOrAfter(dayjs(start_date));
        }
      )
      .test('is-valid', 'Invalid date', function (value) {
        if (value) return !!dayjs(value).isValid();
        return true;
      }),
  }),

  initialState: {
    value: {
      id_department: [],
      id_team: [],
      user_id: [],
      id_yacht: [],
      type: null,
      priority: [],
      status: { label: 'OPEN', id: 'OPEN' },
      start_date: dayjs.utc().startOf('year').format('YYYY-MM-DD'),
      end_date: dayjs.utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
    },
  },

  fields: [
     {
      id: 'type',
      name: 'type',
      type: 'autocomplete',
      label: 'Type',
      options: TypeOptions,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'id_department',
      name: 'id_department',
      type: 'async_autocomplete_multiple',
      label: 'Department',
      api: handoverAPI.departments,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'id_team',
      name: 'id_team',
      type: 'async_autocomplete_multiple',
      label: 'Team',
      api: handoverAPI.teams,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'user_id',
      name: 'user_id',
      type: 'async_autocomplete_multiple',
      label: 'User',
      api: usersNauticaAPI.selectName,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'id_yacht',
      name: 'id_yacht',
      type: 'async_autocomplete_multiple',
      label: 'Yacht',
      api: yachtsAPI.selectNoDRAFT,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'priority',
      name: 'priority',
      type: 'autocomplete_multiple',
      label: 'Priority',
      options: PriorityOptions,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'status',
      name: 'status',
      type: 'autocomplete',
      label: 'Status',
      options: StatusOptions,
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'start_date',
      name: 'start_date',
      type: 'date',
      label: 'Start Date',
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
    {
      id: 'end_date',
      name: 'end_date',
      type: 'date',
      label: 'End Date',
      required: false,
      xs: 12,
      sm: 6,
      md: 4,
    },
  ],

  query: {
    keys: [
      { key: 'type', value: 'type.id', operator: '$eq' },
      { key: 'id_department.id', value: 'id_department.id', operator: '$eq' },
      { key: 'id_team.id', value: 'id_team.id', operator: '$eq' },
      { key: 'id_yacht.id', value: 'id_yacht.id', operator: '$eq' },
      { key: 'priority', value: 'priority.id', operator: '$eq' },
      { key: 'status', value: 'status.id', operator: '$eq' },
      { key: 'createdAt', value: 'start_date', operator: '$gte' },
      { key: 'createdAt', value: 'end_date', operator: '$lte' },
    ],
  },
};

export const SendMailConf = {
  schema: yup.object({
    type: yup.mixed().nullable(),
    id_department: yup.array().nullable(),
    id_team: yup.array().nullable(),
    user_id: yup.array().nullable(),
    id_yacht: yup.array().nullable(),
    priority: yup.array().nullable(),
    status: yup.mixed().nullable(),
    start_date: yup.mixed().nullable(),
    end_date: yup.mixed().nullable(),
    emails: yup
      .array()
      .of(
        yup.object({
          email: yup
            .string()
            .trim()
            .email('Invalid email'),
        })
      )
      .test(
        'at-least-one-valid-email',
        'At least one valid email is required',
        (emails) =>
          Array.isArray(emails) &&
          emails.some((item) => {
            const email = item?.email?.trim();
            return !!email && yup.string().email().isValidSync(email);
          })
      )
      .required('At least one email is required'),
  }),

  fields: [
    ...FilterConf.fields.map((field) => ({
      ...field,
      disabled: true,
    })),
    {
      id: 'emails',
      name: 'emails',
      type: 'add_more_form_fields',
      label: '',
      buttonLabel: 'Add new email',
      xs: 12,
      options: [
        {
          id: 'email',
          name: 'email',
          type: 'text',
          label: 'E-mail',
          xs: 12,
          sm: 12,
          md: 12,
        },
      ],
    },
  ],
};